import React, { useState, useEffect, useRef } from 'react';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { RefreshCw, CheckCircle, XCircle, Clock, AlertCircle, Calendar, Upload, FileSpreadsheet } from 'lucide-react';
import api from '../lib/api';
import { toast } from 'sonner';

const NinetyNineAcresSync = () => {
  const [syncing, setSyncing] = useState(false);
  const [syncingMonth, setSyncingMonth] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [monthSyncResult, setMonthSyncResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchSyncStatus();
    // Refresh status every 30 seconds
    const interval = setInterval(fetchSyncStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchSyncStatus = async () => {
    try {
      const response = await api.get('/integrations/99acres/status');
      setLogs(response.data.logs || []);
    } catch (error) {
      console.error('Failed to fetch sync status');
    }
  };

  const handleCSVUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await api.post('/integrations/99acres/import-csv', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setImportResult(response.data);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message || 'Import failed');
      }
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
      setImportResult({ success: false, message: error.response?.data?.detail || 'Import failed' });
    } finally {
      setImporting(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleManualSync = async () => {
    setSyncing(true);
    try {
      const response = await api.post('/integrations/99acres/sync');
      toast.success(response.data.message);
      
      // Refresh status after a few seconds
      setTimeout(fetchSyncStatus, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sync');
    } finally {
      setSyncing(false);
    }
  };

  const handleCurrentMonthSync = async () => {
    setSyncingMonth(true);
    setMonthSyncResult(null);
    try {
      const response = await api.post('/integrations/99acres/sync-current-month');
      setMonthSyncResult(response.data);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
      // Refresh status
      fetchSyncStatus();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync current month');
    } finally {
      setSyncingMonth(false);
    }
  };

  const testConnection = async () => {
    setLoading(true);
    try {
      const response = await api.post('/integrations/99acres/test');
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error('Connection test failed');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    if (status === 'success') {
      return <CheckCircle className="w-4 h-4 text-green-600" />;
    }
    return <XCircle className="w-4 h-4 text-red-600" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const lastSync = logs.length > 0 ? logs[0] : null;

  return (
    <Card className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <RefreshCw className="w-6 h-6 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-slate-900">99acres Lead Sync</h3>
            <p className="text-sm text-slate-600">
              Import leads via CSV or sync automatically
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={testConnection}
            disabled={loading}
          >
            {loading ? 'Testing...' : 'Test Connection'}
          </Button>
          <Button
            size="sm"
            onClick={handleManualSync}
            disabled={syncing}
            className="bg-blue-600 hover:bg-blue-700"
            data-testid="sync-99acres-button"
          >
            {syncing ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Syncing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Sync Now
              </>
            )}
          </Button>
        </div>
      </div>

      {/* CSV Import Section - Primary method */}
      <div className="mb-6 p-4 bg-green-50 rounded-lg border border-green-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileSpreadsheet className="w-5 h-5 text-green-600" />
            <div>
              <h4 className="font-medium text-slate-900">Import from CSV</h4>
              <p className="text-sm text-slate-600">Download responses from 99acres and upload the CSV file</p>
            </div>
          </div>
          <div>
            <input
              type="file"
              accept=".csv,.CSV"
              onChange={handleCSVUpload}
              ref={fileInputRef}
              className="hidden"
              id="csv-upload"
            />
            <Button
              size="sm"
              onClick={() => fileInputRef.current?.click()}
              disabled={importing}
              className="bg-green-600 hover:bg-green-700"
              data-testid="import-csv-button"
            >
              {importing ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload CSV
                </>
              )}
            </Button>
          </div>
        </div>
        {importResult && (
          <div className="mt-4 pt-4 border-t border-green-200">
            <div className="flex items-center gap-2 mb-2">
              {importResult.success ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <XCircle className="w-4 h-4 text-red-600" />
              )}
              <span className="text-sm font-medium">{importResult.message}</span>
            </div>
            {importResult.stats && (
              <div className="grid grid-cols-4 gap-4 text-center mt-3">
                <div>
                  <div className="text-lg font-bold text-slate-900">{importResult.stats.total || 0}</div>
                  <div className="text-xs text-slate-500">Total Rows</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-green-600">{importResult.stats.imported || 0}</div>
                  <div className="text-xs text-slate-500">Imported</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-yellow-600">{importResult.stats.duplicates || 0}</div>
                  <div className="text-xs text-slate-500">Duplicates</div>
                </div>
                <div>
                  <div className="text-lg font-bold text-red-600">{importResult.stats.errors || 0}</div>
                  <div className="text-xs text-slate-500">Errors</div>
                </div>
              </div>
            )}
            {importResult.sample_leads && importResult.sample_leads.length > 0 && (
              <div className="mt-3 text-xs text-slate-600">
                <span className="font-medium">Sample imported:</span> {importResult.sample_leads.map(l => l.name).join(', ')}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Current Month Sync Section */}
      <div className="mb-6 p-4 bg-indigo-50 rounded-lg border border-indigo-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Calendar className="w-5 h-5 text-indigo-600" />
            <div>
              <h4 className="font-medium text-slate-900">Fetch Current Month Leads</h4>
              <p className="text-sm text-slate-600">Pull all leads from the 1st of this month to today</p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={handleCurrentMonthSync}
            disabled={syncingMonth}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="sync-current-month-button"
          >
            {syncingMonth ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Fetching...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4 mr-2" />
                Fetch Current Month
              </>
            )}
          </Button>
        </div>
        {monthSyncResult && (
          <div className="mt-4 pt-4 border-t border-indigo-200">
            <div className="flex items-center gap-2 mb-2">
              {monthSyncResult.success ? (
                <CheckCircle className="w-4 h-4 text-green-600" />
              ) : (
                <XCircle className="w-4 h-4 text-red-600" />
              )}
              <span className="text-sm font-medium">{monthSyncResult.period?.month}</span>
            </div>
            <p className="text-sm text-slate-600 mb-2">{monthSyncResult.message}</p>
            <div className="grid grid-cols-4 gap-4 text-center">
              <div>
                <div className="text-lg font-bold text-slate-900">{monthSyncResult.stats?.total || 0}</div>
                <div className="text-xs text-slate-500">Total</div>
              </div>
              <div>
                <div className="text-lg font-bold text-green-600">{monthSyncResult.stats?.new || 0}</div>
                <div className="text-xs text-slate-500">New</div>
              </div>
              <div>
                <div className="text-lg font-bold text-yellow-600">{monthSyncResult.stats?.duplicate || 0}</div>
                <div className="text-xs text-slate-500">Duplicate</div>
              </div>
              <div>
                <div className="text-lg font-bold text-red-600">{monthSyncResult.stats?.error || 0}</div>
                <div className="text-xs text-slate-500">Error</div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Last Sync Status */}
      {lastSync && (
        <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              {getStatusIcon(lastSync.status)}
              <span className="text-sm font-medium text-slate-900">Last Sync</span>
            </div>
            <span className="text-xs text-slate-500">
              {formatDate(lastSync.timestamp)}
            </span>
          </div>
          <p className="text-sm text-slate-600 mb-3">{lastSync.message}</p>
          <div className="grid grid-cols-4 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-slate-900">
                {lastSync.stats?.total || 0}
              </div>
              <div className="text-xs text-slate-600">Total</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">
                {lastSync.stats?.new || 0}
              </div>
              <div className="text-xs text-slate-600">New Leads</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {lastSync.stats?.duplicate || 0}
              </div>
              <div className="text-xs text-slate-600">Duplicates</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">
                {lastSync.stats?.error || 0}
              </div>
              <div className="text-xs text-slate-600">Errors</div>
            </div>
          </div>
        </div>
      )}

      {/* Sync History */}
      <div>
        <h4 className="text-sm font-semibold text-slate-900 mb-3">Sync History</h4>
        <div className="space-y-2">
          {logs.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              No sync history available
            </p>
          ) : (
            logs.slice(0, 5).map((log) => (
              <div
                key={log.id || log.timestamp || log.message}
                className="flex items-center justify-between p-3 bg-white border border-slate-200 rounded-lg hover:bg-slate-50"
              >
                <div className="flex items-center gap-3">
                  {getStatusIcon(log.status)}
                  <div>
                    <p className="text-sm text-slate-900">{log.message}</p>
                    <p className="text-xs text-slate-500">
                      {formatDate(log.timestamp)}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className="text-xs">
                    {log.stats?.new || 0} new
                  </Badge>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Info Note */}
      <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-2">
        <AlertCircle className="w-4 h-4 text-blue-600 mt-0.5" />
        <div>
          <p className="text-xs text-blue-900 font-medium">Automatic Sync Active</p>
          <p className="text-xs text-blue-700 mt-1">
            Leads are automatically pulled from 99acres every 10 minutes. 
            Use "Sync Now" to manually trigger an immediate sync.
          </p>
        </div>
      </div>
    </Card>
  );
};

export default NinetyNineAcresSync;
